<template>
    <div class="panel">
        <h3>Ryanair verification</h3>

        <Checkbox v-model="booking.ryanair.enabled" density="compact">
            Force ryanair flights
        </Checkbox>

        <TransitionExpand>
            <div class="panel-select" v-if="booking.ryanair.enabled">
                <div class="dev-panel-dropdown margin">
                    <Select
                        v-model="booking.ryanair.isConfirmed"
                        :items="ryanairOptions"
                        label="Is confirmed" />
                </div>

                <div class="dev-panel-dropdown margin">
                    <Select
                        v-model="booking.ryanair.hasUrl"
                        :items="ryanairOptions"
                        label="Has a verification url" />
                </div>

                <div class="dev-panel-dropdown margin">
                    <Select
                        v-model="booking.ryanair.isUrlValid"
                        :items="ryanairOptions"
                        label="Valid verification url" />
                </div>
            </div>
        </TransitionExpand>
    </div>

    <div class="panel">
        <h3>Booking data</h3>

        <div>
            <Checkbox v-model="booking.createdToday" density="compact">
                Created today
            </Checkbox>
            <Checkbox v-model="booking.canDisplayPaymentReminder" density="compact">
                Can display payment reminder
            </Checkbox>
            <Checkbox v-model="booking.hasServicePackage" density="compact">
                Has service level
            </Checkbox>
            <TransitionExpand>
                <div v-if="booking.hasServicePackage" class="dev-panel-dropdown margin">
                    <Select
                        v-model="booking.servicePackageSelection"
                        :items="servicePackageOptions"
                        class="indent"
                        label="Service type" />
                </div>
            </TransitionExpand>
            <Checkbox v-model="booking.hasCustomerServiceMessages" density="compact">
                Has customer service messages
            </Checkbox>
            <Checkbox v-model="booking.hasImportantInformation" density="compact">
                Has important information
            </Checkbox>
            <Checkbox v-model="booking.hasLeaBankPayment" density="compact">
                Has Lea Bank payment
            </Checkbox>
            <Checkbox v-model="booking.hasAmountAlreadyPaid" density="compact">
                Has amount already paid
            </Checkbox>

            <Checkbox v-model="booking.hasDownPayment" density="compact">
                Has down payment
            </Checkbox>
            <Checkbox
                v-model="booking.hasFinalPayment"
                density="compact"
                :disabled="booking.hasDownPayment">
                Has final payment
            </Checkbox>
            <TransitionExpand>
                <Checkbox
                    v-if="booking.hasFinalPayment"
                    v-model="booking.hasCloseFinalPayment"
                    density="compact"
                    class="indent">
                    Close final payment
                </Checkbox>
            </TransitionExpand>
        </div>

        <div class="dev-panel-dropdown margin">
            <Select
                v-model="booking.status"
                :items="statuses"
                label="Status"
                placeholder="Choose status" />
        </div>
    </div>
    <div class="panel">
        <h3>Session data</h3>
        <Checkbox v-model="session.hasServicePackage" density="compact">
            Has service level
        </Checkbox>
        <TransitionExpand>
            <Checkbox
                v-if="session.hasServicePackage"
                v-model="session.hasServicePackageBasic"
                density="compact"
                class="indent">
                Basic
            </Checkbox>
        </TransitionExpand>
        <TransitionExpand>
            <Checkbox
                v-if="session.hasServicePackage"
                v-model="session.hasServicePackagePremium"
                density="compact"
                class="indent">
                Premium
            </Checkbox>
        </TransitionExpand>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, PropType, watch } from 'vue';
    import { FakeDataBooking, FakeDataBookingSession } from '../DevPanel.interfaces';
    import {
        getMyBookingRyanairOptions,
        getMyBookingServicePackageOptions,
        getMyBookingStatuses,
    } from '../DevPanel.helpers';
    import Checkbox from '@ui/components/FormV2/Fields/Checkbox/Checkbox.vue';
    import Select from '@ui/components/FormV2/Fields/Select/Select.vue';
    import TransitionExpand from '@ui/components/TransitionExpand/TransitionExpand.vue';

    export default defineComponent({
        name: 'BookingPanel',
        components: { Checkbox, Select, TransitionExpand },
        props: {
            booking: { type: Object as PropType<FakeDataBooking>, required: true },
            session: { type: Object as PropType<FakeDataBookingSession>, required: true },
        },
        setup(props) {
            const statuses = computed(() => getMyBookingStatuses());
            const ryanairOptions = computed(() => getMyBookingRyanairOptions());
            const servicePackageOptions = computed(() => getMyBookingServicePackageOptions());

            watch(
                props.booking,
                (newVal) => {
                    if (newVal.hasDownPayment) {
                        props.booking.hasFinalPayment = true;
                    }
                },
                { immediate: true }
            );

            return { statuses, ryanairOptions, servicePackageOptions };
        },
    });
</script>

<style lang="scss">
    @forward './Panel.scss';
</style>
